import React, { PropsWithChildren, useContext, useEffect, useRef } from "react";
import { Box, SxProps, Theme, Typography } from "@mui/material";
import Delimiter from "../../../common/forms/delimiter/Delimiter";
import { PageHeaderHeightContext } from "../DealsPageLayout";

type Props = PropsWithChildren<{
  title?: string;
  subTitle?: string;
  showDelimiter?: boolean;
  leftAction?: React.ReactNode;
  onHeightChange?: (height: number) => void;
  isSidebar?: boolean;
}>;

export const PageHeader = (props: Props) => {
  const {
    title,
    subTitle,
    showDelimiter = true,
    leftAction,
    isSidebar = false,
    onHeightChange,
    children,
  } = props;
  const containerRef = useRef(null);
  const [titleForDisplay, setTitleForDisplay] = React.useState<string>("");

  const pageHeaderHeight = useContext(PageHeaderHeightContext);

  useEffect(() => {
    if (!isSidebar && containerRef.current && onHeightChange) {
      // if the page header is the main header, we need to notify the parent component of the height for use in sizing sidebar headers
      onHeightChange(containerRef.current.offsetHeight);
    }
  }, [containerRef]);

  useEffect(() => {
    if (title) {
      if (isSidebar) {
        if (title.length < 20) {
          setTitleForDisplay(title);
        } else {
          setTitleForDisplay(`${title.substring(0, 20)} ...`);
        }
      } else {
        setTitleForDisplay(title);
      }
    }
  }, [title]);

  const container: SxProps<Theme> = {
    display: "flex",
    flexDirection: "column",
    marginRight: "5px",
    height: isSidebar ? pageHeaderHeight + 1 : "auto",
  };

  const titleAndActionContainer: SxProps<Theme> = {
    flex: 1,
    width: "100%",
    display: "flex",
    alignItems: "flex-end",
  };

  const leftContainer: SxProps<Theme> = {
    flex: isSidebar || children ? 6 : 1,
    flexShrink: 1,
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-end",
  };

  const rightContainer: SxProps<Theme> = {
    flex: 1,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  };

  return (
    <Box ref={containerRef} sx={container}>
      <Box sx={titleAndActionContainer}>
        <Box sx={leftContainer}>
          {leftAction && leftAction}
          {title && (
            <Typography
              variant="h1"
              data-testid="dealkit-deals-title-container"
            >
              {titleForDisplay}
            </Typography>
          )}
        </Box>
        {children && <Box sx={rightContainer}>{children}</Box>}
      </Box>
      <Box>
        <Typography variant="subtitle1">{subTitle}</Typography>
        {showDelimiter && <Delimiter type="headline" shift="18" />}
      </Box>
    </Box>
  );
};
